import React from "react";
import { Link } from "react-router-dom";

import "./app_privacy_policy_page.css";

import Logo from "../assets/composiq-mark-128.png";

export default function AppPrivacyPolicyPage() {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <div className="App">
      <header className="dbx-header" role="banner">
        <div className="dbx-header-inner">
          <Link to="/" className="dbx-brand" aria-label="Zerivo home">
            <img className="dbx-brand-mark" src={Logo} alt="" />
            <div className="dbx-brand-text-group">
              <span className="dbx-brand-text">Zerivo</span>
              <span className="dbx-brand-subtitle">Private AI</span>
            </div>
          </Link>

          <nav className="dbx-header-nav" aria-label="Main navigation">
            <Link to="/our-team">Our Team</Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </div>
      </header>

      {mobileOpen && (
        <nav className="dbx-mobile-nav" role="navigation" aria-label="Mobile navigation">
          <button className="dbx-mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">×</button>
          <Link to="/our-team" onClick={() => setMobileOpen(false)}>Our Team</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)}>Contact</Link>
          <Link to="/privacy" onClick={() => setMobileOpen(false)}>Privacy</Link>
          <Link to="/terms" onClick={() => setMobileOpen(false)}>Terms</Link>
        </nav>
      )}
      {mobileOpen && <div className="dbx-mobile-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />}

      <main className="app-privacy-wrapper">
        <h1>Privacy Policy — Zerivo Application</h1>

        <div className="app-privacy-meta">
          <span><strong>Effective Date:</strong> March 19, 2026</span>
          <span><strong>Last Updated:</strong> March 22, 2026</span>
          <span><strong>Version:</strong> 1.1</span>
        </div>

        <hr />

        {/* ── Section 1 ── */}
        <h2>1. Who We Are</h2>
        <p>
          sparkshare.io Corp ("Zerivo," "we," "us," or "our") provides a zero-egress AI workspace
          deployed entirely within your organization's Microsoft Azure tenant. This Privacy Policy
          explains how data is handled when you use the Zerivo platform ("Service").
        </p>
        <p>
          This policy covers the Zerivo application. For information about data collected through
          our marketing website (zerivo.io), please see our{" "}
          <Link to="/privacy">Website Privacy Policy</Link>.
        </p>
        <p>
          <strong>Data Controller:</strong> Your organization (the entity that subscribes to
          Zerivo) controls all data processed through the Service. Your organization determines
          the purposes and means of processing.
        </p>
        <p>
          <strong>Data Processor:</strong> sparkshare.io Corp operates as a data processor,
          providing the software that runs within your Azure infrastructure. We do not have standing
          access to your data.
        </p>
        <div className="app-privacy-contact">
          <p><strong>Contact:</strong></p>
          <p>Privacy inquiries: <a href="mailto:support@sparkshare.io">support@sparkshare.io</a></p>
          <p>sparkshare.io Corp — 15435 Carpenter Rd, Brookfield, Wisconsin, United States</p>
        </div>

        <hr />

        {/* ── Section 2 ── */}
        <h2>2. Data We Process</h2>

        <h3>2.1 Data Processed Within Your Environment</h3>
        <p>
          The following data is processed within your Azure tenant. Zerivo's software facilitates
          this processing; your organization retains full control.
        </p>

        <div className="app-privacy-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data Category</th>
                <th>Examples</th>
                <th>Purpose</th>
                <th>Retention</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Account &amp; Identity</strong></td>
                <td>User ID, display name, email, tenant ID, assigned role</td>
                <td>Authentication, access control, audit attribution</td>
                <td>Account lifetime</td>
              </tr>
              <tr>
                <td><strong>Chat Content</strong></td>
                <td>User prompts, AI-generated responses</td>
                <td>AI chat and document intelligence</td>
                <td>Configurable by your administrator</td>
              </tr>
              <tr>
                <td><strong>Uploaded Documents</strong></td>
                <td>PDFs, Word files, extracted text, search embeddings</td>
                <td>Document analysis and retrieval</td>
                <td>Until your administrator deletes the project</td>
              </tr>
              <tr>
                <td><strong>Audit Metadata</strong></td>
                <td>Event IDs, actor IDs, session IDs, timestamps, cryptographic hashes</td>
                <td>Compliance, accountability, tamper detection</td>
                <td>Configurable by your administrator (append-only once written; retention period can only be increased)</td>
              </tr>
              <tr>
                <td><strong>User Preferences</strong></td>
                <td>Theme, display settings</td>
                <td>Personalization</td>
                <td>Until account is deprovisioned</td>
              </tr>
              <tr>
                <td><strong>Session Cookie</strong></td>
                <td>Signed session identifier</td>
                <td>Maintaining your authenticated session</td>
                <td>Browser session; cleared on logout</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          All data listed above is stored exclusively within your organization's Azure resources.
          sparkshare.io Corp does not store or have access to this data.
        </p>

        <h3>2.2 Data We Do NOT Collect</h3>
        <p>Zerivo does <strong>not</strong> collect, transmit, or process:</p>
        <ul>
          <li><strong>Analytics or telemetry</strong> — No third-party analytics services run within the application</li>
          <li><strong>Advertising data</strong> — No ad trackers, pixels, or retargeting tags</li>
          <li><strong>Cross-site tracking</strong> — No third-party cookies</li>
          <li><strong>Device fingerprints</strong> — No browser or device fingerprinting</li>
          <li><strong>Model training data</strong> — Your data is never used to train, fine-tune, or improve any AI model</li>
          <li><strong>Behavioral profiling</strong> — No usage pattern analytics or user profiling</li>
          <li><strong>Payment information</strong> — Billing is managed entirely by Microsoft through the Azure Marketplace</li>
        </ul>

        <hr />

        {/* ── Section 3 ── */}
        <h2>3. How Your Data Is Used</h2>
        <p>Data processed through Zerivo is used exclusively to provide the Service you requested:</p>

        <div className="app-privacy-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Purpose</th>
                <th>Lawful Basis (GDPR)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Provide AI chat and document intelligence</td>
                <td>Contractual necessity (Art. 6(1)(b))</td>
              </tr>
              <tr>
                <td>Authenticate users and enforce access controls</td>
                <td>Contractual necessity (Art. 6(1)(b))</td>
              </tr>
              <tr>
                <td>Maintain tamper-evident audit trail for compliance</td>
                <td>Legitimate interest (Art. 6(1)(f)) — regulatory compliance</td>
              </tr>
              <tr>
                <td>Store user display preferences</td>
                <td>Contractual necessity (Art. 6(1)(b))</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p><strong>We do NOT use your data to:</strong></p>
        <ul>
          <li>Train, fine-tune, or improve any AI model</li>
          <li>Build user profiles or behavioral analytics</li>
          <li>Serve advertisements</li>
          <li>Sell or share data with third parties</li>
          <li>Make automated decisions with legal or similarly significant effects</li>
        </ul>

        <hr />

        {/* ── Section 4 ── */}
        <h2>4. Data Security</h2>

        <h3>4.1 Zero-Egress Architecture</h3>
        <p>
          Zerivo is deployed entirely within your Azure tenant. Your data does not leave your
          infrastructure.
        </p>
        <ul>
          <li><strong>Network isolation:</strong> All services run inside your Virtual Network with security groups enforcing deny-all-outbound rules</li>
          <li><strong>Private endpoints:</strong> All Azure services (AI, database, storage, key management) are accessed exclusively via private endpoints — no public internet transit</li>
          <li><strong>No external calls:</strong> The platform makes no calls to sparkshare.io Corp servers, external analytics services, or third-party APIs</li>
          <li><strong>Self-verifiable:</strong> You can verify zero-egress yourself using standard Azure diagnostic tools</li>
        </ul>

        <h3>4.2 Encryption</h3>
        <div className="app-privacy-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Layer</th>
                <th>Standard</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>In transit</td>
                <td>TLS 1.2+ on all connections, including internal service-to-service</td>
              </tr>
              <tr>
                <td>At rest (infrastructure)</td>
                <td>AES-256 via Azure Storage Service Encryption</td>
              </tr>
              <tr>
                <td>At rest (audit payloads)</td>
                <td>AES-256-GCM with per-event encryption keys managed in Azure Key Vault</td>
              </tr>
              <tr>
                <td>Session cookies</td>
                <td>HMAC-SHA256 signed; HttpOnly, Secure, SameSite flags</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>4.3 Access Controls</h3>
        <ul>
          <li>
            <strong>Authentication:</strong> Microsoft Entra ID with OAuth 2.0 and PKCE; supports
            Multi-Factor Authentication as configured by your organization.
          </li>
          <li>
            <strong>Role-based access:</strong> Three-tier model (User, Auditor, Admin) enforced on
            every API endpoint
            <ul>
              <li><strong>Users</strong> interact with AI chat and documents within assigned projects</li>
              <li><strong>Auditors</strong> can search the audit ledger and reveal encrypted content with a written justification</li>
              <li><strong>Admins</strong> have full access including user management and compliance controls</li>
            </ul>
          </li>
          <li>
            <strong>Justified access:</strong> Viewing encrypted audit content requires a written
            justification, which is itself permanently recorded
          </li>
        </ul>

        <hr />

        {/* ── Section 5 ── */}
        <h2>5. Audit Trail and Accountability</h2>
        <p>Every interaction with the Service generates a tamper-evident audit record:</p>
        <ul>
          <li><strong>Append-only storage:</strong> Audit records are written once. The Service provides no interface to modify or delete them, and any alteration made outside the Service is detectable through the hash chain described below</li>
          <li><strong>Hash chains:</strong> Each record is cryptographically linked to the previous record in its session, creating verifiable proof of completeness and ordering</li>
          <li><strong>Metadata only:</strong> The audit ledger contains event IDs, actor IDs, timestamps, and cryptographic hashes — never the content of prompts or responses</li>
          <li><strong>Content separation:</strong> Prompt and response content is stored separately with AES-256-GCM encryption, accessible only with the appropriate role and a logged justification</li>
          <li><strong>Continuous verification:</strong> An automated verifier continuously validates the integrity of the hash chain and audit records</li>
          <li><strong>Export:</strong> Audit records can be exported as CSV or PDF for compliance reviews, legal holds, or investigations</li>
        </ul>

        <hr />

        {/* ── Section 6 ── */}
        <h2>6. Data Retention and Deletion</h2>

        <div className="app-privacy-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data Type</th>
                <th>Retention</th>
                <th>How It's Deleted</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Chat messages</td>
                <td>Until user or administrator deletes</td>
                <td>Removed from your database</td>
              </tr>
              <tr>
                <td>Uploaded documents</td>
                <td>Until project is deleted</td>
                <td>Removed from your database and file storage</td>
              </tr>
              <tr>
                <td>Audit metadata</td>
                <td>Set by your administrator (append-only)</td>
                <td>Not deletable through the Service — by design, for compliance evidence</td>
              </tr>
              <tr>
                <td>Audit content payloads</td>
                <td>Matches audit metadata retention period</td>
                <td>Retained for the compliance retention period; encrypted with AES-256-GCM and accessible only to authorized roles with logged justification</td>
              </tr>
              <tr>
                <td>Account data</td>
                <td>Account lifetime</td>
                <td>Removed when user is deprovisioned</td>
              </tr>
              <tr>
                <td>User preferences</td>
                <td>Account lifetime</td>
                <td>Removed on account deprovisioning</td>
              </tr>
              <tr>
                <td>Session cookies</td>
                <td>Browser session</td>
                <td>Cleared on logout or session expiry</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          <strong>Right to erasure:</strong> Chat content and documents can be deleted by your
          organization's administrator. Audit metadata cannot be deleted because it contains only
          cryptographic hashes and identifiers — not personal content — and serves as your compliance
          evidence. Audit content payloads are encrypted with AES-256-GCM and accessible only to
          authorized roles with a logged justification.
        </p>

        <hr />

        {/* ── Section 7 ── */}
        <h2>7. Your Rights</h2>
        <p>
          Because your organization is the data controller, rights requests should be directed to
          your organization's administrator, who has the tools to fulfill them within the platform.
        </p>

        <h3>Under GDPR (EU/EEA Individuals)</h3>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong> your personal data (Art. 15) — your administrator can search and export records through the audit interface</li>
          <li><strong>Rectification</strong> of inaccurate data (Art. 16) — contact your administrator</li>
          <li><strong>Erasure</strong> (Art. 17) — your administrator can delete chat content and documents; audit metadata is retained as it contains no personal content</li>
          <li><strong>Restriction</strong> of processing (Art. 18) — contact your administrator</li>
          <li><strong>Data portability</strong> (Art. 20) — export functionality is available to administrators</li>
          <li><strong>Object</strong> to processing (Art. 21) — contact your administrator</li>
          <li><strong>Lodge a complaint</strong> with your local Data Protection Authority (<a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">directory</a>)</li>
        </ul>
        <p>
          <strong>Automated decision-making (Art. 22):</strong> Zerivo does not make automated
          decisions with legal or similarly significant effects. AI responses are informational and
          require human review before action.
        </p>

        <h3>Under CCPA/CPRA (California Residents)</h3>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Know</strong> what personal information is collected — see Section 2</li>
          <li><strong>Delete</strong> your personal information — your administrator can delete chat content and documents; audit metadata is retained as it contains no personal content</li>
          <li><strong>Correct</strong> inaccurate information — via your administrator</li>
          <li><strong>Opt-out of sale/sharing</strong> — sparkshare.io Corp does <strong>not</strong> sell or share personal information</li>
        </ul>

        <p><strong>Categories of personal information collected in the preceding 12 months:</strong></p>
        <ul>
          <li>Identifiers (user ID, email, display name)</li>
          <li>Internet or electronic network activity (chat interactions — stored in your tenant only)</li>
          <li>Professional information (organizational role)</li>
        </ul>

        <p><strong>Sources:</strong> Your organization's Microsoft Entra ID directory.</p>
        <p><strong>Third parties receiving personal information:</strong> None. All data remains in your Azure tenant.</p>
        <p><strong>Sale of personal information:</strong> We do <strong>not</strong> sell personal information.</p>
        <p>
          For questions regarding your rights under California privacy law, contact the California Attorney General at{" "}
          <a href="https://oag.ca.gov/privacy" target="_blank" rel="noopener noreferrer">oag.ca.gov/privacy</a>.
        </p>

        <h3>Under HIPAA (If Applicable)</h3>
        <p>
          When your organization is a HIPAA Covered Entity and has executed a Business Associate
          Agreement (BAA) with sparkshare.io Corp:
        </p>
        <ul>
          <li>Zerivo adheres to the Minimum Necessary standard</li>
          <li>Audit metadata contains no Protected Health Information (PHI)</li>
          <li>PHI remains encrypted and within your tenant at all times</li>
          <li>Individual rights are exercised through your organization as the Covered Entity</li>
          <li>Breach notification responsibilities are defined in the BAA</li>
        </ul>

        <h3>Under FERPA (If Applicable)</h3>
        <p>When your organization is an educational institution:</p>
        <ul>
          <li>Zerivo operates under the School Official exception — deployed in your tenant, under your direct control</li>
          <li>Parent and student rights are exercised through your institution</li>
          <li>Zerivo does not independently collect education records</li>
        </ul>

        <hr />

        {/* ── Section 8 ── */}
        <h2>8. Cookies and Tracking Technologies</h2>
        <p>
          Zerivo uses a <strong>single, essential session cookie</strong> to maintain your
          authenticated session.
        </p>

        <div className="app-privacy-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Cookie</th>
                <th>Type</th>
                <th>Purpose</th>
                <th>Duration</th>
                <th>Security</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Session cookie</td>
                <td>First-party, essential</td>
                <td>Authentication</td>
                <td>Browser session</td>
                <td>HttpOnly, Secure, SameSite, HMAC-signed</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p><strong>We do not use:</strong></p>
        <ul>
          <li>Third-party cookies</li>
          <li>Tracking pixels or web beacons</li>
          <li>Local storage for tracking</li>
          <li>Browser fingerprinting</li>
          <li>Analytics or advertising cookies</li>
        </ul>

        <hr />

        {/* ── Section 9 ── */}
        <h2>9. Subprocessors</h2>
        <p>All infrastructure services operate within your Azure tenant:</p>

        <div className="app-privacy-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Purpose</th>
                <th>Data Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Microsoft Azure Cosmos DB</td>
                <td>Data storage</td>
                <td>Your tenant</td>
              </tr>
              <tr>
                <td>Microsoft Azure Blob Storage</td>
                <td>Document and audit storage</td>
                <td>Your tenant</td>
              </tr>
              <tr>
                <td>Microsoft Azure OpenAI Service</td>
                <td>AI inference (chat, embeddings)</td>
                <td>Your tenant via private endpoint</td>
              </tr>
              <tr>
                <td>Microsoft Azure Key Vault</td>
                <td>Encryption key management</td>
                <td>Your tenant</td>
              </tr>
              <tr>
                <td>Microsoft Azure Container Apps</td>
                <td>Application hosting</td>
                <td>Your tenant</td>
              </tr>
              <tr>
                <td>Microsoft Entra ID</td>
                <td>Authentication and identity</td>
                <td>Your directory</td>
              </tr>
              <tr>
                <td>Microsoft Azure Container Registry</td>
                <td>Application container image hosting</td>
                <td>Your tenant</td>
              </tr>
              <tr>
                <td>Microsoft Azure Log Analytics</td>
                <td>Container and infrastructure log collection</td>
                <td>Your tenant via private endpoint</td>
              </tr>
              <tr>
                <td>Microsoft Azure Monitor</td>
                <td>Infrastructure health and diagnostics</td>
                <td>Your tenant via private endpoint</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          All infrastructure services listed above operate entirely within your Azure tenant.
          sparkshare.io Corp does not send data to its own servers or any third-party service.
          We will notify your organization's administrator before adding any new subprocessor.
        </p>

        <hr />

        {/* ── Section 10 ── */}
        <h2>10. International Data Transfers</h2>
        <p>
          All data remains in the Azure region your organization selected during deployment.
          sparkshare.io Corp does not transfer data across borders.
        </p>
        <p>
          If your deployment uses geo-redundant storage or multi-region replication, data may
          replicate to your selected secondary region — this is configured by your Azure
          administrator, not by Zerivo.
        </p>
        <p>
          For GDPR: if your Azure region is within the EU/EEA, your data remains within the
          EU/EEA. No Standard Contractual Clauses are required because no cross-border transfer
          occurs.
        </p>

        <hr />

        {/* ── Section 11 ── */}
        <h2>11. Children's Privacy</h2>
        <p>
          Zerivo does not knowingly collect personal information directly from children under 13
          (COPPA) or under 16 (GDPR). When deployed by an educational institution for K-12 use,
          all FERPA protections described in Section 7 apply.
        </p>

        <hr />

        {/* ── Section 12 ── */}
        <h2>12. AI-Specific Disclosures</h2>

        <h3>Transparency</h3>
        <p>
          Zerivo uses large language models provided by Microsoft Azure OpenAI Service. Users
          should be aware that:
        </p>
        <ul>
          <li>Responses are AI-generated and may contain inaccuracies</li>
          <li>All AI outputs should be reviewed by a human before being relied upon</li>
          <li>The AI does not take autonomous actions — all outputs are presented for user review</li>
          <li>When documents are used, responses include citations to the source material</li>
        </ul>

        <h3>No Model Training</h3>
        <p>
          Your data is <strong>never</strong> used to train, fine-tune, or improve any AI model.
          Azure OpenAI Service does not train on customer data. sparkshare.io Corp does not maintain
          its own models or training pipelines. Your prompts, responses, and documents are never sent
          outside your Azure tenant.
        </p>

        <h3>EU AI Act (Effective August 2, 2026)</h3>
        <p>
          Zerivo is a general-purpose AI productivity tool and is not classified as a high-risk AI
          system under Annex III of the EU AI Act. Technical documentation is available upon request.
        </p>

        <hr />

        {/* ── Section 13 ── */}
        <h2>13. Our Access to Your Environment</h2>
        <p>
          sparkshare.io Corp does <strong>not</strong> have standing access to your Zerivo
          deployment:
        </p>
        <ul>
          <li>We cannot view your data, prompts, responses, documents, or audit records</li>
          <li>We cannot access your environment without your explicit, time-limited authorization</li>
          <li>Any support access is granted by your administrator and fully logged in your audit trail</li>
          <li>Support access is automatically revoked after the authorized period expires</li>
        </ul>

        <hr />

        {/* ── Section 14 ── */}
        <h2>14. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy to reflect changes in the Service or legal requirements.
          When we make material changes:
        </p>
        <ul>
          <li>We will update the "Last Updated" date at the top of this page</li>
          <li>We will notify your organization's administrator via email</li>
          <li>Material changes take effect 30 days after notification</li>
        </ul>
        <p>
          Continued use of the Service after the effective date constitutes acceptance of the
          updated policy.
        </p>

        <hr />

        {/* ── Section 15 ── */}
        <h2>15. Contact Us</h2>
        <p>For privacy inquiries, data subject requests, or complaints:</p>
        <div className="app-privacy-contact">
          <p><strong>Email:</strong> <a href="mailto:support@sparkshare.io">support@sparkshare.io</a></p>
          <p><strong>Entity:</strong> sparkshare.io Corp — 15435 Carpenter Rd Brookfield, Wisconsin, United States</p>
        </div>

        <p className="app-privacy-footer-note">
          This privacy policy reflects the Zerivo platform as of version 1.1.
        </p>
      </main>
    </div>
  );
}
